import {
  coerceAvatarCardId,
  DEFAULT_AVATAR,
  isExclusiveAvatarCard,
  usernameOwnsExclusiveAvatar,
} from './catalog'
import { coerceAvatarFrameId } from './frames'
import type { AvatarConfig } from './types'

/** Positive integer small enough to travel in JSON without becoming a float. */
function exclusiveRev(cardId: string, raw: unknown): number | undefined {
  if (!isExclusiveAvatarCard(cardId)) return undefined
  const n = typeof raw === 'number' ? raw : NaN
  if (!Number.isInteger(n) || n < 1 || n > 1_000_000_000) return undefined
  return n
}

function avatarOf(cardId: string, frameId: string, revRaw: unknown): AvatarConfig {
  const rev = exclusiveRev(cardId, revRaw)
  return rev === undefined ? { cardId, frameId } : { cardId, frameId, rev }
}

/**
 * Coerce unknown JSON into a legal avatar. Bad or missing data → default.
 */
export function parseAvatarConfig(raw: unknown): AvatarConfig {
  if (typeof raw === 'string') {
    return { cardId: coerceAvatarCardId(raw), frameId: DEFAULT_AVATAR.frameId }
  }
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_AVATAR }
  const o = raw as Record<string, unknown>
  if (typeof o.cardId !== 'string') return { ...DEFAULT_AVATAR }
  return avatarOf(coerceAvatarCardId(o.cardId), coerceAvatarFrameId(o.frameId), o.rev)
}

/**
 * Same as {@link parseAvatarConfig}, but the crx portrait is kept only
 * for that account. Everyone else snaps back to Blue-Eyes.
 */
export function parseAvatarForUser(raw: unknown, username: string): AvatarConfig {
  const avatar = parseAvatarConfig(raw)
  if (
    isExclusiveAvatarCard(avatar.cardId) &&
    !usernameOwnsExclusiveAvatar(username)
  ) {
    return { cardId: DEFAULT_AVATAR.cardId, frameId: avatar.frameId }
  }
  return avatar
}
